import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Send, Sparkles, ArrowLeft, Loader2, FileDown, MessageSquarePlus, Archive, X, Trash2, Eye } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { DictationButton } from '../components/DictationButton';
import BottomNav from '../components/BottomNav';
import { exportChatPDF, exportChatTXT } from '../utils/exportUtils';

export const Coaching = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [pendingInitialMessage, setPendingInitialMessage] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Archive drawer state
  const [archiveDrawerOpen, setArchiveDrawerOpen] = useState(false);
  const [archives, setArchives] = useState([]);
  const [archivesLoading, setArchivesLoading] = useState(false);
  const [viewingArchive, setViewingArchive] = useState(null); // {id, title, messages, archived_at}
  const [viewingArchiveLoading, setViewingArchiveLoading] = useState(false);

  const messagesEndRef = useRef(null);

  const hasRealHistory = () => {
    // Count user messages — welcome message is excluded because it's auto-generated
    return messages.some((m) => m.role === 'user');
  };

  // Load archives list
  const loadArchives = async () => {
    setArchivesLoading(true);
    try {
      const res = await api.get('/coaching/archives');
      setArchives(res.data.archives || []);
    } catch (error) {
      toast.error(language === 'tr' ? 'Arşivler yüklenemedi' : 'Failed to load archives');
    } finally {
      setArchivesLoading(false);
    }
  };

  const openArchiveDrawer = async () => {
    setArchiveDrawerOpen(true);
    await loadArchives();
  };

  const closeArchiveDrawer = () => {
    setArchiveDrawerOpen(false);
    setViewingArchive(null);
  };

  const handleArchiveCurrent = async () => {
    if (!hasRealHistory()) {
      toast.info(language === 'tr' ? 'Arşivlenecek sohbet yok' : 'No conversation to archive');
      return;
    }
    try {
      await api.post('/coaching/archive');
      setMessages([{ role: 'assistant', content: t('coaching.welcomeMessage') }]);
      toast.success(language === 'tr' ? 'Sohbet arşivlendi' : 'Chat archived');
    } catch (error) {
      toast.error(language === 'tr' ? 'Arşivleme başarısız' : 'Archive failed');
    }
  };

  const handleNewChat = async () => {
    if (hasRealHistory()) {
      // Auto-archive current conversation before starting new
      try {
        await api.post('/coaching/archive');
      } catch (error) {
        // If archiving fails, fall back to clearing
        try {
          await api.delete('/coaching/history');
        } catch {}
      }
    }
    setMessages([{ role: 'assistant', content: t('coaching.welcomeMessage') }]);
    toast.success(language === 'tr' ? 'Yeni sohbet başlatıldı' : 'New chat started');
  };

  const handleViewArchive = async (archiveId) => {
    setViewingArchiveLoading(true);
    try {
      const res = await api.get(`/coaching/archives/${archiveId}`);
      setViewingArchive(res.data.archive);
    } catch (error) {
      toast.error(language === 'tr' ? 'Arşiv yüklenemedi' : 'Failed to load archive');
    } finally {
      setViewingArchiveLoading(false);
    }
  };

  const handleDeleteArchive = async (archiveId, title) => {
    const confirmMsg = language === 'tr'
      ? `"${title}" arşivi silinsin mi? Bu işlem geri alınamaz.`
      : `Delete the archive "${title}"? This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.delete(`/coaching/archives/${archiveId}`);
      setArchives((prev) => prev.filter((a) => a.id !== archiveId));
      if (viewingArchive?.id === archiveId) setViewingArchive(null);
      toast.success(language === 'tr' ? 'Arşiv silindi' : 'Archive deleted');
    } catch (error) {
      toast.error(language === 'tr' ? 'Silme başarısız' : 'Delete failed');
    }
  };

  // Export chat handlers (for current live conversation)
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      await exportChatPDF(messages, language);
      toast.success(language === 'tr' ? 'PDF indirildi!' : 'PDF downloaded!');
    } catch (error) {
      toast.error(language === 'tr' ? 'Dışa aktarma başarısız' : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportTXT = () => {
    try {
      exportChatTXT(messages, language);
      toast.success(language === 'tr' ? 'TXT indirildi!' : 'TXT downloaded!');
    } catch (error) {
      toast.error(language === 'tr' ? 'Dışa aktarma başarısız' : 'Export failed');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const initialMsg = location.state?.initialMessage;
    if (initialMsg) {
      setPendingInitialMessage(initialMsg);
      window.history.replaceState({}, document.title);
    }
    loadConversationHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!initialLoading && pendingInitialMessage && messages.length > 0) {
      const autoSend = async () => {
        const msg = pendingInitialMessage;
        setPendingInitialMessage(null);
        setMessages((prev) => [...prev, { role: 'user', content: msg }]);
        setLoading(true);
        try {
          const response = await api.post('/coaching/chat', { message: msg });
          setMessages((prev) => [...prev, { role: 'assistant', content: response.data.response }]);
        } catch (error) {
          toast.error(t('coaching.failed'));
          setMessages((prev) => [...prev, { role: 'assistant', content: t('coaching.errorResponse') }]);
        } finally {
          setLoading(false);
        }
      };
      autoSend();
    }
  }, [initialLoading, pendingInitialMessage, messages.length, t]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversationHistory = async () => {
    try {
      const response = await api.get('/coaching/history');
      const history = response.data.messages || [];
      if (history.length > 0) {
        setMessages([
          { role: 'assistant', content: language === 'tr'
            ? 'Tekrar hoş geldiniz. Kaldığımız yerden devam edelim...'
            : "Welcome back. Let's continue where we left off..." },
          ...history,
        ]);
      } else {
        setMessages([{ role: 'assistant', content: t('coaching.welcomeMessage') }]);
      }
    } catch (error) {
      setMessages([{ role: 'assistant', content: t('coaching.welcomeMessage') }]);
    } finally {
      setInitialLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async (messageText) => {
    if (!messageText?.trim() || loading) return;
    setMessages((prev) => [...prev, { role: 'user', content: messageText }]);
    setLoading(true);
    try {
      const response = await api.post('/coaching/chat', { message: messageText });
      setMessages((prev) => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      toast.error(t('coaching.failed'));
      setMessages((prev) => [...prev, { role: 'assistant', content: t('coaching.errorResponse') }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const userMessage = input;
    setInput('');
    sendMessage(userMessage);
  };

  const formatArchiveDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const locale = language === 'tr' ? 'tr-TR' : 'en-US';
      return d.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return iso.substring(0, 10);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative flex flex-col pb-32 lg:pb-0" style={{ position: 'relative', zIndex: 1 }}>
      {/* Header */}
      <div
        className="sticky top-0 z-10 glass-panel border-b border-[#D4AF37]/10 px-6 pb-3"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}
      >
        <div className="max-w-4xl mx-auto">
          {/* Top row: back + title (leave right side clear for floating language globe) */}
          <div className="flex items-center justify-between pr-12">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-white/70 hover:text-white transition-colors duration-300 uppercase tracking-widest text-sm font-sans flex items-center gap-2"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('nav.dashboard')}
            </button>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#D4AF37]" />
              <h1 className="font-serif font-light text-xl text-[#F8F5F2]">
                {t('coaching.title')}
              </h1>
            </div>
          </div>

          {/* Action row: New / Archive list always available; right-aligned, below globe */}
          <div className="flex items-center justify-end gap-2 mt-4 pr-0 sm:pr-14">
            {/* New Chat */}
            <button
              onClick={handleNewChat}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all text-xs font-sans"
              data-testid="new-chat-btn"
            >
              <MessageSquarePlus className="w-3.5 h-3.5" />
              <span>{language === 'tr' ? 'Yeni' : 'New'}</span>
            </button>
            {/* Archive (drawer) */}
            <button
              onClick={openArchiveDrawer}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/30 transition-all text-xs font-sans"
              data-testid="archive-chat-btn"
            >
              <Archive className="w-3.5 h-3.5" />
              <span>{language === 'tr' ? 'Arşiv' : 'Archive'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {messages.map((msg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              data-testid={`message-${index}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl p-4 ${
                  msg.role === 'user'
                    ? 'bg-[#D4AF37]/20 border border-[#D4AF37]/30'
                    : 'glass-panel'
                }`}
              >
                <p className="font-sans font-light text-[#F8F5F2] whitespace-pre-wrap leading-relaxed">
                  {msg.content?.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1')}
                </p>
              </div>
            </motion.div>
          ))}
          {loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
            >
              <div className="glass-panel rounded-2xl p-4">
                <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" />
              </div>
            </motion.div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className="sticky bottom-0 glass-panel border-t border-[#D4AF37]/10 px-6 py-4 pb-6 mb-12 lg:mb-0">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="flex gap-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('coaching.placeholder')}
              className="flex-1 bg-white/5 border border-[#D4AF37]/15 rounded-full px-6 py-3 text-[#F8F5F2] font-sans focus-visible:ring-2 focus-visible:ring-[#D4AF37]/50 focus-visible:outline-none"
              disabled={loading}
              data-testid="coach-input"
            />
            <DictationButton onTranscript={(text) => setInput(input + (input ? ' ' : '') + text)} />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="rounded-full border border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] px-6 py-3 hover:bg-[#D4AF37] hover:text-[#030305] transition-all duration-300 disabled:opacity-50 flex items-center gap-2"
              data-testid="send-button"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
      <BottomNav />

      {/* Archive Drawer */}
      <AnimatePresence>
        {archiveDrawerOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              style={{ zIndex: 70 }}
              onClick={closeArchiveDrawer}
              data-testid="archive-backdrop"
            />
            {/* Drawer */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-[#07070c] border-l border-[#D4AF37]/20 shadow-2xl flex flex-col"
              style={{
                zIndex: 71,
                paddingTop: 'env(safe-area-inset-top, 0px)',
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              }}
              data-testid="archive-drawer"
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between p-5 border-b border-white/10 shrink-0">
                <div className="flex items-center gap-2">
                  <Archive className="w-5 h-5 text-[#D4AF37]" />
                  <h2 className="font-serif text-xl text-[#F8F5F2]">
                    {viewingArchive
                      ? (language === 'tr' ? 'Arşivlenen Sohbet' : 'Archived Chat')
                      : (language === 'tr' ? 'Arşivler' : 'Archives')}
                  </h2>
                </div>
                <button
                  onClick={viewingArchive ? () => setViewingArchive(null) : closeArchiveDrawer}
                  className="w-9 h-9 rounded-full hover:bg-white/5 text-[#A1A1AA] hover:text-white flex items-center justify-center transition-colors"
                  data-testid="archive-drawer-close"
                >
                  {viewingArchive ? <ArrowLeft className="w-4 h-4" /> : <X className="w-5 h-5" />}
                </button>
              </div>

              {/* Drawer Body */}
              <div className="flex-1 overflow-y-auto">
                {viewingArchive ? (
                  /* Archived conversation viewer */
                  <div className="p-5 space-y-4" data-testid="archive-viewer">
                    <div className="glass-panel rounded-xl p-4 space-y-2">
                      <p className="text-[#F8F5F2] font-sans text-sm">{viewingArchive.title}</p>
                      <p className="text-[#6B7280] text-xs">
                        {formatArchiveDate(viewingArchive.archived_at)} · {viewingArchive.message_count || viewingArchive.messages?.length || 0} {language === 'tr' ? 'mesaj' : 'messages'}
                      </p>
                    </div>
                    {(viewingArchive.messages || []).map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-2xl p-3 ${
                            msg.role === 'user'
                              ? 'bg-[#D4AF37]/15 border border-[#D4AF37]/25'
                              : 'bg-white/5 border border-white/10'
                          }`}
                        >
                          <p className="font-sans font-light text-[#F8F5F2] whitespace-pre-wrap text-sm leading-relaxed">
                            {msg.content?.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Archive list */
                  <div className="p-5">
                    {/* Export current live chat section */}
                    {hasRealHistory() && (
                      <div className="mb-6">
                        <p className="text-[#6B7280] text-xs uppercase tracking-widest font-sans mb-3">
                          {language === 'tr' ? 'Mevcut Sohbet' : 'Current Chat'}
                        </p>
                        <div className="glass-panel rounded-xl p-4 flex flex-wrap gap-2">
                          <button
                            onClick={handleArchiveCurrent}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-all text-xs font-sans"
                            data-testid="archive-now-btn"
                          >
                            <Archive className="w-3.5 h-3.5" />
                            {language === 'tr' ? 'Şimdi Arşivle' : 'Archive Now'}
                          </button>
                          <button
                            onClick={handleExportPDF}
                            disabled={exporting}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/30 transition-all text-xs font-sans"
                            data-testid="export-pdf-btn"
                          >
                            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                            {language === 'tr' ? 'PDF' : 'PDF'}
                          </button>
                          <button
                            onClick={handleExportTXT}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/30 transition-all text-xs font-sans"
                            data-testid="export-txt-btn"
                          >
                            <FileDown className="w-3.5 h-3.5" />
                            {language === 'tr' ? 'TXT' : 'TXT'}
                          </button>
                        </div>
                      </div>
                    )}

                    <p className="text-[#6B7280] text-xs uppercase tracking-widest font-sans mb-3">
                      {language === 'tr' ? 'Geçmiş Sohbetler' : 'Past Conversations'}
                    </p>

                    {archivesLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
                      </div>
                    ) : archives.length === 0 ? (
                      <div className="text-center py-12 px-4" data-testid="archives-empty">
                        <Archive className="w-10 h-10 text-[#6B7280] mx-auto mb-3 opacity-50" />
                        <p className="text-[#A1A1AA] text-sm font-sans">
                          {language === 'tr'
                            ? 'Henüz arşivlenmiş sohbet yok. Yeni bir konuşma başlayıp "Arşivle"ye basabilirsiniz.'
                            : "No archived chats yet. Start a conversation and hit \"Archive\" to save it here."}
                        </p>
                      </div>
                    ) : (
                      <ul className="space-y-2" data-testid="archives-list">
                        {archives.map((arc) => (
                          <li
                            key={arc.id}
                            className="glass-panel rounded-xl p-3 flex items-start gap-3 hover:bg-white/5 transition-colors"
                            data-testid={`archive-item-${arc.id}`}
                          >
                            <button
                              onClick={() => handleViewArchive(arc.id)}
                              className="flex-1 text-left min-w-0"
                              data-testid={`archive-view-${arc.id}`}
                            >
                              <p className="text-[#F8F5F2] font-sans text-sm font-medium truncate">
                                {arc.title}
                              </p>
                              <p className="text-[#6B7280] text-xs mt-0.5">
                                {formatArchiveDate(arc.archived_at)} · {arc.message_count || 0} {language === 'tr' ? 'mesaj' : 'messages'}
                              </p>
                              {arc.preview && (
                                <p className="text-[#A1A1AA] text-xs mt-1 line-clamp-1">
                                  {arc.preview}
                                </p>
                              )}
                            </button>
                            <div className="flex flex-col gap-1 shrink-0">
                              <button
                                onClick={() => handleViewArchive(arc.id)}
                                disabled={viewingArchiveLoading}
                                className="w-8 h-8 rounded-lg hover:bg-[#D4AF37]/10 text-[#A1A1AA] hover:text-[#D4AF37] flex items-center justify-center transition-colors"
                                title={language === 'tr' ? 'Görüntüle' : 'View'}
                                data-testid={`archive-view-icon-${arc.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteArchive(arc.id, arc.title)}
                                className="w-8 h-8 rounded-lg hover:bg-red-500/10 text-[#A1A1AA] hover:text-red-400 flex items-center justify-center transition-colors"
                                title={language === 'tr' ? 'Sil' : 'Delete'}
                                data-testid={`archive-delete-${arc.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Coaching;
